import React, { useMemo, useRef, useState } from "react";
import "./MultiSelectChips.css";

// Searchable multi-select with removable chips:
//   [ Jajmau × ] [ Deoria × ] [ type to search...      ]
// Selected options are hidden from the list, so a value can never be picked
// twice. The option list renders inline (not as a floating menu) so it is
// never clipped by a scrollable Bootstrap <Modal> body.
//
// `value` is an array of option values (compared as strings); `onChange`
// receives the next array. `inputId` lets a <Form.Label htmlFor> point at
// the search box.
export default function MultiSelectChips({
  inputId,
  options = [],
  value = [],
  onChange,
  placeholder = "Search...",
  disabled = false,
  loading = false,
  noOptionsText = "No matches",
  isInvalid = false,
  ariaDescribedBy,
}) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const inputRef = useRef(null);
  const listId = `${inputId}-listbox`;

  const selectedKeys = useMemo(() => value.map(String), [value]);
  const byKey = useMemo(() => new Map(options.map((o) => [String(o.value), o])), [options]);
  const selected = selectedKeys.map((k) => byKey.get(k) || { value: k, label: k });

  const q = query.trim().toLowerCase();
  const available = options.filter(
    (o) => !selectedKeys.includes(String(o.value)) && (!q || String(o.label).toLowerCase().includes(q))
  );

  const add = (opt) => {
    const key = String(opt.value);
    if (selectedKeys.includes(key)) return;
    onChange?.([...selectedKeys, key]);
    setQuery("");
    inputRef.current?.focus();
  };

  const remove = (key) => onChange?.(selectedKeys.filter((k) => k !== String(key)));

  const onKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (available[0]) add(available[0]);
    } else if (e.key === "Backspace" && !query && selectedKeys.length) {
      remove(selectedKeys[selectedKeys.length - 1]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div className={`multi-chips${disabled ? " is-disabled" : ""}${isInvalid ? " is-invalid" : ""}`}>
      <div className="multi-chips-control" onClick={() => !disabled && inputRef.current?.focus()}>
        {selected.map((opt) => (
          <span key={opt.value} className="multi-chip">
            <span className="multi-chip-label">{opt.label}</span>
            {!disabled && (
              <button
                type="button"
                className="multi-chip-remove"
                aria-label={`Remove ${opt.label}`}
                onClick={(e) => {
                  e.stopPropagation();
                  remove(opt.value);
                }}
              >
                ×
              </button>
            )}
          </span>
        ))}
        <input
          ref={inputRef}
          id={inputId}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-describedby={ariaDescribedBy}
          className="multi-chips-input"
          placeholder={selected.length ? "" : (loading ? "Loading..." : placeholder)}
          value={query}
          disabled={disabled}
          autoComplete="off"
          onFocus={() => setOpen(true)}
          onBlur={() => setOpen(false)}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onKeyDown={onKeyDown}
        />
      </div>
      {open && !disabled && (
        <ul id={listId} role="listbox" aria-multiselectable="true" className="multi-chips-menu">
          {available.length === 0 ? (
            <li className="multi-chips-empty">{loading ? "Loading..." : noOptionsText}</li>
          ) : (
            available.map((opt) => (
              <li
                key={opt.value}
                role="option"
                aria-selected="false"
                className="multi-chips-option"
                // mousedown keeps focus in the input, so the list stays open
                // for picking several values in a row.
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => add(opt)}
              >
                {opt.label}
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
